import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Phone, 
  Mail, 
  Circle, 
  CheckCircle2,
  Clock,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Plane,
  Hotel,
  Car,
  Ship,
  Utensils
} from 'lucide-react';
import './ClientPortalView.css';

const ClientPortalView = ({ quote, checklist: initialChecklist, schedule, onChecklistUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [checklist, setChecklist] = useState(initialChecklist || []);

  // Parse travel components from quote
  const travelComponents = quote?.components ? 
    (typeof quote.components === 'string' ? JSON.parse(quote.components) : quote.components) : [];

  // Generate day-by-day schedule from components
  const generateDayByDaySchedule = () => {
    if (!quote?.travel_start_date || !quote?.travel_end_date) return [];
    
    // Parse YYYY-MM-DD strings as calendar dates
    const [startYear, startMonth, startDay] = quote.travel_start_date.split('-');
    const [endYear, endMonth, endDay] = quote.travel_end_date.split('-');
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    const days = [];
    
    // Generate array of all days in the trip
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      days.push({
        date: dateStr,
        dateObj: new Date(d),
        components: []
      });
    }
    
    // Assign components to days
    travelComponents.forEach(component => {
      const data = component.data || {};
      
      switch (component.type) {
        case 'hotel':
          if (data.checkInDate && data.checkOutDate) {
            const checkIn = new Date(data.checkInDate);
            const checkOut = new Date(data.checkOutDate);
            
            days.forEach(day => {
              if (day.dateObj >= checkIn && day.dateObj <= checkOut) {
                const isCheckIn = day.dateObj.toISOString().split('T')[0] === data.checkInDate;
                const isCheckOut = day.dateObj.toISOString().split('T')[0] === data.checkOutDate;
                
                day.components.push({
                  ...component,
                  dayLabel: isCheckIn ? 'Check-in' : (isCheckOut ? 'Check-out' : null),
                  time: isCheckIn ? data.checkInTime : null
                });
              }
            });
          }
          break;
          
        case 'flight': {
          const isRoundTrip = data.tripType === 'round_trip';
          if (data.departureDate) {
            const flightDay = days.find(d => d.date === data.departureDate);
            if (flightDay) {
              flightDay.components.push({
                ...component,
                dayLabel: isRoundTrip ? 'Outbound Departure' : 'Departure',
                time: data.departureTime
              });
            }
          }
          if (isRoundTrip && data.returnDepartureDate) {
            const returnDay = days.find(d => d.date === data.returnDepartureDate);
            if (returnDay) {
              returnDay.components.push({
                ...component,
                dayLabel: 'Return Departure',
                time: data.returnDepartureTime
              });
            }
          }
          break;
        }
          
        case 'car':
          if (data.pickupDate && data.returnDate) {
            const pickup = new Date(data.pickupDate);
            const returnDate = new Date(data.returnDate);
            
            days.forEach(day => {
              if (day.dateObj >= pickup && day.dateObj <= returnDate) {
                const isPickup = day.dateObj.toISOString().split('T')[0] === data.pickupDate;
                const isReturn = day.dateObj.toISOString().split('T')[0] === data.returnDate;
                
                day.components.push({
                  ...component,
                  dayLabel: isPickup ? 'Pickup' : (isReturn ? 'Return' : null),
                  time: isPickup ? data.pickupTime : null
                });
              }
            });
          }
          break;
          
        case 'cruise':
          if (data.embarkDate && data.disembarkDate) {
            const embark = new Date(data.embarkDate);
            const disembark = new Date(data.disembarkDate);
            
            days.forEach(day => {
              if (day.dateObj >= embark && day.dateObj <= disembark) {
                const isEmbark = day.dateObj.toISOString().split('T')[0] === data.embarkDate;
                const isDisembark = day.dateObj.toISOString().split('T')[0] === data.disembarkDate;
                
                day.components.push({
                  ...component,
                  dayLabel: isEmbark ? 'Embark' : (isDisembark ? 'Disembark' : null),
                  time: isEmbark ? data.embarkTime : null
                });
              }
            });
          }
          break;
          
        case 'activity':
        case 'tour':
          if (data.date) {
            const activityDay = days.find(d => d.date === data.date);
            if (activityDay) {
              activityDay.components.push({
                ...component,
                time: data.time
              });
            }
          }
          break;
          
        default:
          // Fallback for components with simple date field
          if (component.date) {
            const compDay = days.find(d => d.date === component.date);
            if (compDay) {
              compDay.components.push({
                ...component,
                time: component.time
              });
            }
          }
      }
    });
    
    return days;
  };

  // Extract date/time info from component based on type
  const getComponentDateInfo = (component) => {
    const data = component.data || {};
    
    switch (component.type) {
      case 'hotel':
        if (data.checkInDate && data.checkOutDate) {
          return {
            date: data.checkInDate,
            endDate: data.checkOutDate,
            time: data.checkInTime,
            label: 'Check-in',
            endLabel: 'Check-out'
          };
        }
        break;
      case 'flight':
        if (data.departureDate) {
          if (data.tripType === 'round_trip' && data.returnDepartureDate) {
            return {
              date: data.departureDate,
              endDate: data.returnDepartureDate,
              time: data.departureTime,
              label: 'Outbound Departure',
              endLabel: 'Return Departure'
            };
          }
          return {
            date: data.departureDate,
            time: data.departureTime,
            label: 'Departure'
          };
        }
        break;
      case 'car':
        if (data.pickupDate && data.returnDate) {
          return {
            date: data.pickupDate,
            endDate: data.returnDate,
            time: data.pickupTime,
            label: 'Pickup',
            endLabel: 'Return'
          };
        }
        break;
      case 'cruise':
        if (data.embarkDate && data.disembarkDate) {
          return {
            date: data.embarkDate,
            endDate: data.disembarkDate,
            time: data.embarkTime,
            label: 'Embark',
            endLabel: 'Disembark'
          };
        }
        break;
      case 'activity':
      case 'tour':
        if (data.date) {
          return {
            date: data.date,
            time: data.time,
            label: 'Scheduled'
          };
        }
        break;
      default:
        // Fallback to component.date if it exists
        if (component.date) {
          return {
            date: component.date,
            time: component.time,
            label: 'Date'
          };
        }
    }
    
    return null;
  };

  // Calculate trip countdown
  const calculateCountdown = () => {
    if (!quote?.travel_start_date) return null;
    const today = new Date();
    const tripDate = new Date(quote.travel_start_date);
    const diffTime = tripDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Trip in progress';
    if (diffDays === 0) return 'Departing today!';
    if (diffDays === 1) return 'Departing tomorrow!';
    return `${diffDays} days until departure`;
  };

  // Sync checklist with prop changes
  useEffect(() => {
    if (initialChecklist && initialChecklist.length > 0) {
      setChecklist(initialChecklist);
    }
  }, [initialChecklist]);

  // Toggle checklist item
  const toggleChecklistItem = (categoryIndex, itemId) => {
    setChecklist(prev => {
      const newChecklist = [...prev];
      const category = newChecklist[categoryIndex];
      const item = category.items.find(i => i.id === itemId);
      if (item) {
        item.completed = !item.completed;
      }
      
      // Call parent update handler if provided
      if (onChecklistUpdate) {
        onChecklistUpdate(newChecklist);
      }
      
      return newChecklist;
    });
  };

  // Calculate checklist progress
  const getChecklistProgress = () => {
    const allItems = checklist.flatMap(cat => cat.items);
    const completed = allItems.filter(item => item.completed).length;
    const total = allItems.length;
    return { completed, total, percentage: total > 0 ? (completed / total) * 100 : 0 };
  };

  // Calculate payment progress
  const getPaymentProgress = () => {
    const total = parseFloat(quote?.total_amount || 0);
    const paid = parseFloat(quote?.total_paid || 0);
    const remaining = total - paid;
    const percentage = total > 0 ? (paid / total) * 100 : 0;
    
    return { total, paid, remaining, percentage };
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date - expects YYYY-MM-DD string (calendar date, not timestamp)
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get component icon
  const getComponentIcon = (type) => {
    const iconMap = {
      'flight': Plane,
      'hotel': Hotel,
      'car': Car,
      'cruise': Ship,
      'dining': Utensils,
      'activity': MapPin
    };
    const Icon = iconMap[type?.toLowerCase()] || MapPin;
    return <Icon className="w-5 h-5" />;
  };

  // Placeholder hero image
  const heroImage = quote?.destination_image || 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1600&h=900&fit=crop';

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'payments', label: 'Payments' },
    { id: 'checklist', label: 'Checklist' }
  ];

  const progress = getChecklistProgress();
  const paymentProgress = getPaymentProgress();

  return (
    <div className="client-portal">
      {/* Hero Header */}
      <div 
        className="hero-header h-80 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
          <div className="max-w-4xl mx-auto w-full">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
              {quote?.title || 'Your Dream Vacation'}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(quote?.travel_start_date)} - {formatDate(quote?.travel_end_date)}</span>
              </div>
              {calculateCountdown() && (
                <div className="px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium">
                  🎯 {calculateCountdown()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Tab Navigation */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto">
          <nav className="flex gap-8 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[var(--accent)]'
                    : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--primary)]"></div>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Trip Details */}
              <div className="md:col-span-2 space-y-6">
                <div className="bg-white rounded-xl ring-1 ring-[var(--border)] shadow-sm p-6">
                  <h2 className="text-xl font-semibold tracking-tight mb-4">Trip Details</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[var(--muted-foreground)] mt-0.5" />
                      <div>
                        <p className="text-sm text-[var(--muted-foreground)]">Destination</p>
                        <p className="font-medium">{quote?.destination || 'Multiple Destinations'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-[var(--muted-foreground)] mt-0.5" />
                      <div>
                        <p className="text-sm text-[var(--muted-foreground)]">Travelers</p>
                        <p className="font-medium">
                          {((quote?.adults || 0) + (quote?.children || 0)) || 2} {((quote?.adults || 0) + (quote?.children || 0)) === 1 ? 'passenger' : 'passengers'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-[var(--muted-foreground)] mt-0.5" />
                      <div>
                        <p className="text-sm text-[var(--muted-foreground)]">Duration</p>
                        <p className="font-medium">
                          {quote?.travel_start_date && quote?.travel_end_date ? 
                            `${Math.ceil((new Date(quote.travel_end_date) - new Date(quote.travel_start_date)) / (1000 * 60 * 60 * 24))} nights` 
                            : 'TBD'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {quote?.agent_notes && (
                  <div className="bg-[var(--muted)] rounded-xl ring-1 ring-[var(--border)] shadow-sm p-6">
                    <h3 className="text-lg font-semibold tracking-tight mb-2">Special Notes</h3>
                    <p className="text-[var(--foreground)] whitespace-pre-wrap">{quote.agent_notes}</p>
                  </div>
                )}
              </div>

              {/* Agent Profile Card */}
              <div className="bg-white rounded-xl ring-1 ring-[var(--border)] shadow-sm p-6">
                <h2 className="text-xl font-semibold tracking-tight mb-4">Your Travel Agent</h2>
                <div className="space-y-4">
                  {quote?.agent_logo_url && (
                    <img 
                      src={quote.agent_logo_url} 
                      alt="Agent" 
                      className="w-20 h-20 rounded-full object-cover mx-auto ring-2 ring-[var(--border)]"
                    />
                  )}
                  <div className="text-center">
                    <p className="font-semibold text-lg">{quote?.agent_name || 'Your Agent'}</p>
                    {quote?.agent_business_name && (
                      <p className="text-sm text-[var(--muted-foreground)]">{quote.agent_business_name}</p>
                    )}
                  </div>
                  <div className="space-y-3 pt-4 border-t border-[var(--border)]">
                    {quote?.agent_email && (
                      <a 
                        href={`mailto:${quote.agent_email}`}
                        className="flex items-center gap-2 text-sm text-[var(--muted-foreground)] hover:text-[var(--accent)] transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{quote.agent_email}</span>
                      </a>
                    )}
                    {quote?.agent_phone && (
                      <a 
                        href={`tel:${quote.agent_phone}`}
                        className="flex items-center gap-2 text-sm text-[var(--muted-foreground)] hover:text-[var(--accent)] transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{quote.agent_phone}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold tracking-tight">Your Itinerary</h2>
            
            {(() => {
              const dayByDay = generateDayByDaySchedule();
              
              return dayByDay.length > 0 ? (
                <div className="space-y-4">
                  {dayByDay.map((day, dayIndex) => (
                    <div key={dayIndex} className="bg-white rounded-xl ring-1 ring-[var(--border)] shadow-sm overflow-hidden">
                      {/* Day Header */}
                      <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary)] px-6 py-4">
                        <div className="flex items-center gap-2 text-white">
                          <Calendar className="w-5 h-5" />
                          <h3 className="font-semibold text-lg">
                            {formatDate(day.date)}
                          </h3>
                        </div>
                      </div>
                      
                      {/* Day Components */}
                      <div className="p-6">
                        {day.components.length > 0 ? (
                          <div className="space-y-4">
                            {day.components.map((component, compIndex) => {
                              const data = component.data || {};
                              
                              // Get photo for component
                              const getComponentPhoto = () => {
                                // 1. Check for custom day photo from detailed schedule
                                if (schedule && Array.isArray(schedule)) {
                                  const customDay = schedule.find(d => d.date === day.date);
                                  if (customDay && customDay.photo) {
                                    return customDay.photo;
                                  }
                                }
                                
                                // 2. Check for component photos array
                                if (data.photos && data.photos.length > 0) {
                                  return data.photos[0];
                                }
                                
                                // 3. Check for single photo field
                                if (component.photo) {
                                  return component.photo;
                                }
                                
                                // 4. Return null for placeholder
                                return null;
                              };
                              
                              const photo = getComponentPhoto();
                              
                              // Gradient colors by type
                              const getPlaceholderGradient = (type) => {
                                switch(type) {
                                  case 'hotel':
                                    return 'from-blue-400 to-blue-600';
                                  case 'flight':
                                    return 'from-sky-400 to-sky-600';
                                  case 'car':
                                    return 'from-gray-400 to-gray-600';
                                  case 'cruise':
                                    return 'from-cyan-400 to-cyan-600';
                                  case 'activity':
                                  case 'tour':
                                    return 'from-purple-400 to-purple-600';
                                  default:
                                    return 'from-blue-400 to-blue-600';
                                }
                              };
                              
                              return (
                                <div key={compIndex} className="flex items-start gap-4 p-4 bg-[var(--muted)] rounded-lg">
                                  {/* Photo */}
                                  <div className="flex-shrink-0">
                                    {photo ? (
                                      <img 
                                        src={photo} 
                                        alt={component.title || component.type}
                                        className="w-20 h-20 rounded-lg object-cover"
                                      />
                                    ) : (
                                      <div className={`w-20 h-20 rounded-lg bg-gradient-to-br ${getPlaceholderGradient(component.type)} flex items-center justify-center text-white`}>
                                        <div className="text-3xl">
                                          {getComponentIcon(component.type)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <h4 className="font-semibold text-[var(--foreground)]">
                                        {component.title || data.propertyName || data.airline || component.name || component.type}
                                      </h4>
                                      
                                      {/* Show Check-in/Check-out label */}
                                      {component.dayLabel && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--muted)] text-[var(--primary)] flex-shrink-0">
                                          {component.dayLabel}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Time */}
                                    {component.time && (
                                      <div className="flex items-center gap-1 mt-1 text-sm text-[var(--muted-foreground)]">
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>{component.time}</span>
                                      </div>
                                    )}
                                    
                                    {/* Location */}
                                    {(data.location || component.location) && (
                                      <div className="flex items-center gap-1 mt-1 text-sm text-[var(--muted-foreground)]">
                                        <MapPin className="w-3.5 h-3.5" />
                                        <span>{data.location || component.location}</span>
                                      </div>
                                    )}
                                    
                                    {/* Description */}
                                    {component.description && (
                                      <p className="text-sm text-[var(--muted-foreground)] mt-2">{component.description}</p>
                                    )}
                                    
                                    {/* Confirmation number */}
                                    {data.confirmationNumber && (
                                      <div className="mt-2 text-sm text-[var(--muted-foreground)]">
                                        <span className="font-medium">Confirmation:</span> {data.confirmationNumber}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[var(--muted-foreground)] text-sm italic">No scheduled activities</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[var(--muted)] rounded-xl p-12 text-center">
                  <Calendar className="w-12 h-12 text-[var(--border)] mx-auto mb-3" />
                  <p className="text-[var(--muted-foreground)]">Your detailed itinerary will be available soon.</p>
                  <p className="text-sm text-[var(--muted-foreground)] mt-1">Your travel agent is finalizing the schedule.</p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold tracking-tight">Payment Overview</h2>
            
            {/* Payment Progress Bar */}
            <div className="bg-white rounded-xl ring-1 ring-[var(--border)] shadow-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Payment Progress</h3>
                <span className="text-sm text-[var(--muted-foreground)]">
                  {paymentProgress.percentage.toFixed(0)}% Complete
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="h-3 bg-[var(--muted)] rounded-full overflow-hidden mb-4">
                <div 
                  className="h-full bg-gradient-to-r from-[var(--accent)] to-[var(--primary)] transition-all duration-500 ease-out"
                  style={{ width: `${paymentProgress.percentage}%` }}
                ></div>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-[var(--muted-foreground)]">
                  Paid: <span className="font-semibold text-[var(--foreground)]">{formatCurrency(paymentProgress.paid)}</span>
                </span>
                <span className="text-[var(--muted-foreground)]">
                  Total: <span className="font-semibold text-[var(--foreground)]">{formatCurrency(paymentProgress.total)}</span>
                </span>
              </div>
            </div>

            {/* Payment Details */}
            <div className="bg-white rounded-xl ring-1 ring-[var(--border)] shadow-sm overflow-hidden">
              <div className="divide-y divide-[var(--border)]">
                {/* Total Price */}
                <div className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[var(--muted)] rounded-lg">
                      <DollarSign className="w-5 h-5 text-[var(--muted-foreground)]" />
                    </div>
                    <div>
                      <p className="font-medium">Total Trip Price</p>
                      <p className="text-sm text-[var(--muted-foreground)]">All services included</p>
                    </div>
                  </div>
                  <p className="text-xl font-semibold">{formatCurrency(paymentProgress.total)}</p>
                </div>

                {/* Amount Paid */}
                <div className="p-6 flex items-center justify-between bg-green-50/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-900">Amount Paid</p>
                      <p className="text-sm text-green-600">Thank you for your payment</p>
                    </div>
                  </div>
                  <p className="text-xl font-semibold text-green-700">{formatCurrency(paymentProgress.paid)}</p>
                </div>

                {/* Balance Due */}
                {paymentProgress.remaining > 0 && (
                  <div className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-50 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium">Balance Due</p>
                        {quote?.final_payment_date && (
                          <p className="text-sm text-orange-600">
                            Due by {formatDate(quote.final_payment_date)}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-xl font-semibold text-orange-600">{formatCurrency(paymentProgress.remaining)}</p>
                  </div>
                )}
              </div>
            </div>

            {paymentProgress.remaining > 0 && (
              <div className="bg-[var(--muted)] rounded-xl ring-1 ring-[var(--border)] p-6 text-center">
                <p className="text-sm text-[var(--muted-foreground)] mb-4">
                  Need to make a payment? Contact your travel agent for payment options.
                </p>
                <button className="px-6 py-2.5 bg-[var(--primary)] text-white rounded-lg font-medium hover:bg-[var(--primary)] transition-colors">
                  Contact Agent
                </button>
              </div>
            )}
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div className="space-y-6">
            {/* Progress Header */}
            <div className="bg-white rounded-xl ring-1 ring-[var(--border)] shadow-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-semibold tracking-tight">Trip Checklist</h2>
                <span className="text-sm text-[var(--muted-foreground)]">
                  {progress.completed} of {progress.total} completed
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="h-2 bg-[var(--muted)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 ease-out"
                  style={{ width: `${progress.percentage}%` }}
                ></div>
              </div>
            </div>

            {/* Checklist Categories */}
            <div className="space-y-8">
              {checklist.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-3">
                  <h3 className="text-lg font-semibold text-[var(--foreground)] tracking-tight">
                    {category.category}
                  </h3>
                  <div className="space-y-2">
                    {category.items.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => toggleChecklistItem(categoryIndex, item.id)}
                        className="bg-white rounded-lg ring-1 ring-[var(--border)] shadow-sm p-4 hover:bg-[var(--muted)] transition-colors cursor-pointer flex items-center gap-3"
                      >
                        {item.completed ? (
                          <CheckCircle2 className="w-5 h-5 text-[var(--accent)] flex-shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-[var(--border)] flex-shrink-0" />
                        )}
                        <span className={`flex-1 ${item.completed ? 'text-[var(--muted-foreground)] line-through' : 'text-[var(--foreground)]'}`}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortalView;
